import { Expense, ExpenseCategory, BUILT_IN_EXPENSE_CATEGORIES, PlannedPaymentWindow } from '@otw/models'
import { expenseConverter } from '@otw/models/converters'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { db, storage } from 'config/firebase'
import { useAuth } from 'contexts/AuthContext'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { addDoc, collection, deleteDoc, doc, getDocs, updateDoc } from '@firebase/firestore'
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from '@firebase/storage'
import { validateExtractedInvoiceData } from 'views/SuperAdmin/SuperAdminHome/components/ExpenseDialog'
import { useInventory } from './inventory'
import { apiClient } from '@/core/api/apiClient'
import {
  type RepairShopInvoiceImportInput,
} from '@/core/expenses/repairShopInvoices'
import {
  expenseNow,
  getPaidThisWeekExpenses,
  isDateInInclusiveRange,
  type ExpenseDateField,
} from '@/core/expenses/paidWeek'

export {
  expenseNow,
  getPaidExpensesForWeek,
  getPaidThisWeekExpenses,
  getPaidWeekBounds,
  isPaidInWeek,
  isPaidThisWeek,
  resolvePaidAtFromPicker,
  type ExpenseDateField,
  type PaidWeekBounds,
} from '@/core/expenses/paidWeek'

// Helper function to validate expense categories
export const isValidExpenseCategory = (category: string): category is ExpenseCategory => {
  return ['Shipping', 'Repairs', 'Parts', 'Reimbursements', 'RentalReimbursement', 'StorageFees', 'Other'].includes(category)
}

// An expense counts toward money totals only once it has been reviewed.
// Absent reviewStatus ⇒ a normal, manually-created expense (treated as reviewed).
export const isReviewed = (expense: Pick<Expense, 'reviewStatus'>): boolean =>
  !expense.reviewStatus || expense.reviewStatus === 'reviewed'

export const isPlannedForPayment = (expense: Pick<Expense, 'paid' | 'plannedPaymentWindow'>): boolean =>
  expense.paid !== true &&
  (expense.plannedPaymentWindow === 'today' || expense.plannedPaymentWindow === 'this_week')

export const getPlannedPaymentLabel = (
  plannedPaymentWindow: Expense['plannedPaymentWindow'],
): string | null => {
  if (plannedPaymentWindow === 'today') return 'Pay today'
  if (plannedPaymentWindow === 'this_week') return 'Pay this week'
  return null
}

/** Short labels for grid chips and filters. */
export const getPlannedPaymentChipLabel = (
  plannedPaymentWindow: Expense['plannedPaymentWindow'],
): string | null => {
  if (plannedPaymentWindow === 'today') return 'Today'
  if (plannedPaymentWindow === 'this_week') return 'This week'
  return null
}

export const getPlannedPaymentExpenses = (expenses: Expense[]): Expense[] =>
  expenses.filter(expense => isReviewed(expense) && isPlannedForPayment(expense))

/** Sum reviewed expense amounts linked to a repair shop via `repairShopId`. */
export const getRepairShopExpenseTotal = (expenses: Expense[], repairShopId: string): number =>
  expenses
    .filter(expense => isReviewed(expense) && expense.repairShopId === repairShopId)
    .reduce((sum, expense) => sum + expense.amount, 0)

/** Map repair shop id → total reviewed expense amount at that shop. */
export const getRepairShopSpendingById = (expenses: Expense[]): Record<string, number> =>
  expenses
    .filter(isReviewed)
    .reduce<Record<string, number>>((acc, expense) => {
      if (!expense.repairShopId) return acc
      acc[expense.repairShopId] = (acc[expense.repairShopId] ?? 0) + expense.amount
      return acc
    }, {})

const EMPTY_EXPENSES: Expense[] = []

interface UseExpensesProps {
  vin?: string // Optional VIN to filter expenses for a specific vehicle
  dateRange?: {
    start: Date
    end: Date
    /** Which date field the range applies to. Default: expense/invoice `date`. */
    field?: ExpenseDateField
  }
  options?: {
    onSuccess?: () => void
    onError?: (error: Error) => void
    dontShowUpdates?: boolean
    /** When false, skip the expenses query (dashboard mobile gate). */
    enabled?: boolean
    /** Skip the full master_inventory fetch. Dashboard Overview/Inventory reads should set this. */
    skipInventory?: boolean
    onExtractionStart?: () => void
    onExtractionSuccess?: (data: any) => void
    onExtractionError?: (error: Error) => void
  }
}

export const useExpenses = ({ vin, dateRange, options }: UseExpensesProps = {}) => {
  const { userInfo } = useAuth()
  const { showSuccess, showError, showInfo, showSnackbar, showWarning } = useSnackbar()
  const queryClient = useQueryClient()

  const expensesCollection = collection(db, 'expenses').withConverter(expenseConverter)

  const skipInventory = options?.skipInventory === true
  const { vehicles, updateDocument: updateVehicle } = useInventory('master_inventory', undefined, {
    dontShowUpdates: true,
    skipRealtime: skipInventory,
    enabled: !skipInventory,
  })

  // One Firestore read per VIN scope; dateRange is applied in memory (dashboard widgets
  // used to key by range and each triggered a full `getDocs(expenses)`).
  const queryKey = ['expenses', vin ?? null] as const

  const getNormalizedAttachmentPaths = (expense: Partial<Expense>): string[] => {
    const fromArray = Array.isArray(expense.attachmentPaths)
      ? expense.attachmentPaths.filter((path): path is string => typeof path === 'string' && path.length > 0)
      : []
    if (fromArray.length > 0) return fromArray
    return expense.attachmentPath ? [expense.attachmentPath] : []
  }

  const getNormalizedAttachmentURLs = (expense: Partial<Expense>): string[] => {
    const fromArray = Array.isArray(expense.attachmentURLs)
      ? expense.attachmentURLs.filter((url): url is string => typeof url === 'string' && url.length > 0)
      : []
    if (fromArray.length > 0) return fromArray
    return expense.attachmentURL ? [expense.attachmentURL] : []
  }

  const {
    data: expensesQueryData,
    isLoading: expensesQueryLoading,
    isFetching: expensesQueryFetching,
    isError,
    error,
    refetch: refetchExpenses,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      // Get all expenses without filtering in the query
      const querySnapshot = await getDocs(expensesCollection)
      return querySnapshot.docs.map(doc => doc.data())
    },
    enabled: options?.enabled !== false,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    // Poll while any imported invoice is still being extracted so the grid updates live.
    refetchInterval: query => {
      const data = query.state.data as Expense[] | undefined
      return data?.some(expense => expense.reviewStatus === 'processing') ? 4000 : false
    },
  })

  const allExpenses = expensesQueryData ?? EMPTY_EXPENSES
  /** Skeleton only when there is no hydrated/cached data yet. */
  const isLoading = expensesQueryLoading && expensesQueryData === undefined
  const isFetching = expensesQueryFetching

  // Filter expenses by date range and vin (memoized so consumers don't see a new
  // array reference every render — that can retrigger effects and blow the update depth).
  const expenses = useMemo(() => {
    return allExpenses.filter(expense => {
      // Filter by VIN if provided
      if (vin && expense.vehicleId !== vin) {
        return false
      }

      // Filter by date range if provided (expense/invoice date or paidAt)
      if (dateRange) {
        const field = dateRange.field ?? 'date'
        const value =
          field === 'paidAt'
            ? expense.paidAt instanceof Date
              ? expense.paidAt
              : null
            : expense.date
              ? new Date(expense.date)
              : null
        if (!value) return false
        return isDateInInclusiveRange(value, dateRange.start, dateRange.end)
      }

      return true
    })
  }, [
    allExpenses,
    vin,
    dateRange?.start?.getTime?.() ?? dateRange?.start,
    dateRange?.end?.getTime?.() ?? dateRange?.end,
    dateRange?.field,
  ])

  // Helper function to upload expense attachment
  const uploadExpenseAttachments = async (expenseId: string, files: File[]) => {
    // Check if storage is properly initialized
    if (!storage) {
      throw new Error('Firebase Storage is not properly initialized. Please check your Firebase configuration.')
    }

    try {
      const uploaded = await Promise.all(
        files.map(async (file, index) => {
          const safeFileName = file.name.replace(/\s+/g, '_')
          const filePath = `expenses/${expenseId}/${Date.now()}-${index}-${safeFileName}`
          console.log('Uploading file to path:', filePath)

          const storageRef = ref(storage, filePath)
          const uploadTask = await uploadBytesResumable(storageRef, file)
          const downloadURL = await getDownloadURL(uploadTask.ref)

          return { attachmentPath: filePath, attachmentURL: downloadURL }
        }),
      )

      const attachmentPaths = uploaded.map(item => item.attachmentPath)
      const attachmentURLs = uploaded.map(item => item.attachmentURL)
      console.log('Files uploaded successfully:', { count: uploaded.length })
      return { attachmentPaths, attachmentURLs }
    } catch (error) {
      console.error('Error uploading attachment:', error)
      throw new Error(`Failed to upload attachment: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  // Helper function to delete expense attachment
  const deleteExpenseAttachments = async (attachmentPaths: string[]) => {
    if (!attachmentPaths.length) return
    try {
      await Promise.all(
        attachmentPaths.map(async attachmentPath => {
          const storageRef = ref(storage, attachmentPath)
          await deleteObject(storageRef)
        }),
      )
    } catch (error) {
      console.error('Failed to delete attachment:', error)
      // Decide if this should throw or just warn
    }
  }

  // Helper function to update vehicle costs when an expense changes
  const updateVehicleCosts = async (
    vehicleId: string,
    expenseCategory: string,
    amountDifference: number,
  ) => {
    if (!vehicleId) return

    try {
      // Find the vehicle in the cached vehicles data
      const vehicle = vehicles?.find(v => v.id === vehicleId)

      if (!vehicle) {
        console.warn(`No vehicle found with ID: ${vehicleId}`)
        return
      }

      // Update the appropriate cost field based on expense category
      let updateField = {}

      switch (expenseCategory) {
        case 'Shipping':
          updateField = { shipping_cost: (vehicle.shipping_cost || 0) + amountDifference }
          break
        case 'Repairs':
          updateField = { repair_cost: (vehicle.repair_cost || 0) + amountDifference }
          break
        case 'Parts':
          updateField = { repair_cost: (vehicle.repair_cost || 0) + amountDifference }
          break
        case 'RentalReimbursement':
        case 'Reimbursements':
          updateField = { rental_cost: (vehicle.rental_cost || 0) + amountDifference }
          break
        case 'StorageFees':
          updateField = { storage_cost: (vehicle.storage_cost || 0) + amountDifference }
          break
        case 'Other':
          updateField = { other_cost: (vehicle.other_cost || 0) + amountDifference }
          break
        default:
          // Custom (user-created) categories behave like "Other".
          updateField = { other_cost: (vehicle.other_cost || 0) + amountDifference }
          break
      }

      // Use the inventory hook to update the vehicle
      updateVehicle({
        id: vehicle.id,
        data: updateField,
      })
    } catch (error) {
      console.error('Error updating vehicle costs:', error)
    }
  }

  // Add a new expense
  const addExpense = useMutation({
    mutationFn: async ({
      expenseData,
      attachmentFiles,
    }: {
      expenseData: Omit<Expense, 'id' | 'created_by' | 'created_at' | 'updated_at' | 'updated_by'>
      attachmentFiles?: File[] | null
    }) => {
      if (!userInfo) {
        throw new Error('User must be authenticated to add an expense')
      }

      const newExpense: Omit<Expense, 'id'> = {
        ...expenseData,
        paid: expenseData.paid === true,
        creditCardCharge:
          typeof expenseData.creditCardCharge === 'number' && !Number.isNaN(expenseData.creditCardCharge)
            ? expenseData.creditCardCharge
            : null,
        discountType:
          expenseData.discountType === 'percent' || expenseData.discountType === 'amount'
            ? expenseData.discountType
            : null,
        discountValue:
          typeof expenseData.discountValue === 'number' &&
          !Number.isNaN(expenseData.discountValue) &&
          expenseData.discountValue > 0
            ? expenseData.discountValue
            : null,
        paidAt:
          expenseData.paid === true
            ? expenseData.paidAt instanceof Date
              ? expenseData.paidAt
              : expenseNow()
            : null,
        created_by: userInfo.id || userInfo.email || '',
        created_at: new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })),
        updated_at: null,
        updated_by: null,
        customCategory: expenseData.customCategory === undefined ? null : expenseData.customCategory,
        invoiceNumber: expenseData.invoiceNumber === undefined ? null : expenseData.invoiceNumber,
        vendorCategory: expenseData.vendorCategory === undefined ? null : expenseData.vendorCategory,
        businessName: expenseData.businessName || '',
        attachmentPath: null, // Initialize
        attachmentURL: null, // Initialize
        attachmentPaths: null,
        attachmentURLs: null,
        plannedPaymentWindow: null,
      }

      // 1. Add the document to Firestore to get an ID
      const docRef = await addDoc(expensesCollection, newExpense)
      const expenseId = docRef.id

      // Create a complete expense object with the ID
      const completeExpense: Expense = {
        ...newExpense,
        id: expenseId,
      }

      // 2. If there's an attachment, upload it using the expense ID
      if (attachmentFiles?.length) {
        const attachmentInfo = await uploadExpenseAttachments(expenseId, attachmentFiles)

        // 3. Update the Firestore document with attachment info
        await updateDoc(doc(expensesCollection, expenseId), {
          attachmentPath: attachmentInfo.attachmentPaths[0] ?? null,
          attachmentURL: attachmentInfo.attachmentURLs[0] ?? null,
          attachmentPaths: attachmentInfo.attachmentPaths,
          attachmentURLs: attachmentInfo.attachmentURLs,
        })

        // Update the complete expense object with attachment info
        completeExpense.attachmentPath = attachmentInfo.attachmentPaths[0] ?? null
        completeExpense.attachmentURL = attachmentInfo.attachmentURLs[0] ?? null
        completeExpense.attachmentPaths = attachmentInfo.attachmentPaths
        completeExpense.attachmentURLs = attachmentInfo.attachmentURLs
      }

      // Update vehicle costs if this expense is linked to a vehicle
      if (expenseData.vehicleId) {
        await updateVehicleCosts(expenseData.vehicleId, expenseData.category, expenseData.amount)
      }

      return completeExpense // Return the complete expense object with ID and attachment info
    },
    onMutate: async ({ expenseData, attachmentFiles }) => {
      await queryClient.cancelQueries({ queryKey })
      const previousExpenses = queryClient.getQueryData<Expense[]>(queryKey)

      const optimisticExpense: Expense = {
        ...expenseData,
        paid: expenseData.paid === true,
        creditCardCharge:
          typeof expenseData.creditCardCharge === 'number' && !Number.isNaN(expenseData.creditCardCharge)
            ? expenseData.creditCardCharge
            : null,
        discountType:
          expenseData.discountType === 'percent' || expenseData.discountType === 'amount'
            ? expenseData.discountType
            : null,
        discountValue:
          typeof expenseData.discountValue === 'number' &&
          !Number.isNaN(expenseData.discountValue) &&
          expenseData.discountValue > 0
            ? expenseData.discountValue
            : null,
        paidAt:
          expenseData.paid === true
            ? expenseData.paidAt instanceof Date
              ? expenseData.paidAt
              : expenseNow()
            : null,
        id: `temp-${Date.now()}`,
        created_by: userInfo?.id || userInfo?.email || '',
        created_at: new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })),
        updated_at: null,
        updated_by: null,
        customCategory: expenseData.customCategory || null,
        invoiceNumber: expenseData.invoiceNumber || null,
        vendorCategory: expenseData.vendorCategory || null,
        businessName: expenseData.businessName || '',
        attachmentPath: attachmentFiles?.length ? 'uploading...' : null,
        attachmentURL: attachmentFiles?.length ? 'uploading...' : null,
        attachmentPaths: attachmentFiles?.length ? ['uploading...'] : null,
        attachmentURLs: attachmentFiles?.length ? ['uploading...'] : null,
      }

      queryClient.setQueryData<Expense[]>(queryKey, old => {
        if (!old) return [optimisticExpense]
        return [...old, optimisticExpense]
      })

      return { previousExpenses }
    },
    onError: (err, variables, context) => {
      queryClient.setQueryData(queryKey, context?.previousExpenses)
      if (!options?.dontShowUpdates) {
        showError('Error adding expense')
      }
      console.error('Error adding expense:', err)
      options?.onError?.(err instanceof Error ? err : new Error(String(err)))
    },
    onSuccess: data => {
      // Refetch to ensure data consistency
      refetchExpenses()
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
      if (!options?.dontShowUpdates) {
        showSuccess('Expense added successfully')
      }
      options?.onSuccess?.()
    },
  })

  // Update an expense
  const updateExpense = useMutation({
    mutationFn: async ({
      expenseData,
      attachmentFiles,
    }: {
      expenseData: Pick<Expense, 'id'> & Partial<Omit<Expense, 'id' | 'created_by' | 'created_at'>>
      attachmentFiles?: File[] | null
    }) => {
      if (!userInfo) {
        throw new Error('User must be authenticated to update an expense')
      }

      if (!expenseData.id) {
        throw new Error('Expense ID is required for updates')
      }

      // Get the current expense to calculate cost differences
      const currentExpense = allExpenses.find(expense => expense.id === expenseData.id)

      if (!currentExpense) {
        throw new Error(`Expense with ID ${expenseData.id} not found`)
      }

      // Calculate amount difference for vehicle cost updates
      let amountDifference = 0
      if (expenseData.amount !== undefined && currentExpense.amount !== expenseData.amount) {
        amountDifference = expenseData.amount - currentExpense.amount
      }

      // Determine if the category has changed for vehicle cost updates
      const categoryChanged = expenseData.category !== undefined && currentExpense.category !== expenseData.category
      const vehicleIdChanged =
        expenseData.vehicleId !== undefined && currentExpense.vehicleId !== expenseData.vehicleId

      // Merge current expense with update data
      const mergedExpense: Expense = {
        ...currentExpense,
        ...expenseData,
        updated_at: new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })),
        updated_by: userInfo.id || userInfo.email || '',
      }

      // Handle attachment changes
      const currentAttachmentPaths = getNormalizedAttachmentPaths(currentExpense)
      const currentAttachmentURLs = getNormalizedAttachmentURLs(currentExpense)
      const requestedExistingPaths = Array.isArray(expenseData.attachmentPaths)
        ? expenseData.attachmentPaths.filter((path): path is string => typeof path === 'string' && path.length > 0)
        : currentAttachmentPaths
      const requestedExistingURLs = Array.isArray(expenseData.attachmentURLs)
        ? expenseData.attachmentURLs.filter((url): url is string => typeof url === 'string' && url.length > 0)
        : currentAttachmentURLs
      if (attachmentFiles?.length) {
        const attachmentInfo = await uploadExpenseAttachments(expenseData.id, attachmentFiles)
        const pathsToDelete = currentAttachmentPaths.filter(path => !requestedExistingPaths.includes(path))
        await deleteExpenseAttachments(pathsToDelete)
        const finalAttachmentPaths = [...requestedExistingPaths, ...attachmentInfo.attachmentPaths]
        const finalAttachmentURLs = [...requestedExistingURLs, ...attachmentInfo.attachmentURLs]

        // Add paths to merged expense
        mergedExpense.attachmentPath = finalAttachmentPaths[0] ?? null
        mergedExpense.attachmentURL = finalAttachmentURLs[0] ?? null
        mergedExpense.attachmentPaths = finalAttachmentPaths
        mergedExpense.attachmentURLs = finalAttachmentURLs
      } else if (
        expenseData.attachmentPath === null ||
        expenseData.attachmentPaths === null
      ) {
        await deleteExpenseAttachments(currentAttachmentPaths)
        mergedExpense.attachmentPath = null
        mergedExpense.attachmentURL = null
        mergedExpense.attachmentPaths = null
        mergedExpense.attachmentURLs = null
      } else {
        mergedExpense.attachmentPaths = getNormalizedAttachmentPaths(mergedExpense)
        mergedExpense.attachmentURLs = getNormalizedAttachmentURLs(mergedExpense)
        mergedExpense.attachmentPath = mergedExpense.attachmentPaths[0] ?? null
        mergedExpense.attachmentURL = mergedExpense.attachmentURLs[0] ?? null
      }

      const wasPaid = currentExpense.paid === true
      const isPaid = mergedExpense.paid === true
      if (!isPaid) {
        mergedExpense.paidAt = null
      } else if (expenseData.paidAt !== undefined) {
        mergedExpense.paidAt = expenseData.paidAt
      } else if (!wasPaid) {
        mergedExpense.paidAt = expenseNow()
      } else {
        mergedExpense.paidAt = currentExpense.paidAt instanceof Date ? currentExpense.paidAt : expenseNow()
      }

      if (isPaid) {
        mergedExpense.plannedPaymentWindow = null
      } else if (expenseData.plannedPaymentWindow !== undefined) {
        mergedExpense.plannedPaymentWindow =
          expenseData.plannedPaymentWindow === 'today' || expenseData.plannedPaymentWindow === 'this_week'
            ? expenseData.plannedPaymentWindow
            : null
      }

      // Use the converter's toFirestore method to convert the expense (handles Date to ISO string conversion)
      const { id, ...firestoreData } = expenseConverter.toFirestore(mergedExpense)

      // Update in Firestore
      const expenseRef = doc(expensesCollection, expenseData.id)
      await updateDoc(expenseRef, firestoreData)

      // Handle cost updates for vehicles based on the changes.
      //
      // First review save: a pending AI-imported expense (reviewStatus !== 'reviewed') transitions
      // to 'reviewed'. No prior cost has ever been applied to any vehicle for this expense, so we
      // skip the diff math and just apply the full amount fresh to whatever vehicle is set.
      const isFirstReview =
        !!currentExpense.reviewStatus &&
        currentExpense.reviewStatus !== 'reviewed' &&
        mergedExpense.reviewStatus === 'reviewed'

      if (isFirstReview) {
        if (mergedExpense.vehicleId) {
          await updateVehicleCosts(mergedExpense.vehicleId, mergedExpense.category, mergedExpense.amount)
        }
      }
      // 1. If vehicle ID changed, update both old and new vehicles
      else if (vehicleIdChanged) {
        // If there was a previous vehicle, subtract full amount from it
        if (currentExpense.vehicleId) {
          await updateVehicleCosts(currentExpense.vehicleId, currentExpense.category, -currentExpense.amount)
        }

        // If there's a new vehicle, add full amount to it
        if (expenseData.vehicleId) {
          await updateVehicleCosts(
            expenseData.vehicleId,
            expenseData.category || currentExpense.category,
            expenseData.amount || currentExpense.amount,
          )
        }
      }
      // 2. If category changed but same vehicle, update both categories for the vehicle
      else if (categoryChanged && currentExpense.vehicleId) {
        // Subtract from old category
        await updateVehicleCosts(currentExpense.vehicleId, currentExpense.category, -currentExpense.amount)

        // Add to new category
        if (expenseData.category) {
          await updateVehicleCosts(
            currentExpense.vehicleId,
            expenseData.category,
            expenseData.amount || currentExpense.amount,
          )
        }
      }
      // 3. If only amount changed, update the current vehicle with the difference
      else if (amountDifference !== 0 && currentExpense.vehicleId) {
        await updateVehicleCosts(currentExpense.vehicleId, currentExpense.category, amountDifference)
      }

      // Determine final vehicle ID for the returned expense
      const finalVehicleId = expenseData.vehicleId !== undefined ? expenseData.vehicleId : currentExpense.vehicleId

      // Return the updated expense (mergedExpense already has all the updates)
      return {
        ...mergedExpense,
        vehicleId: finalVehicleId,
      } as Expense
    },
    onMutate: async ({ expenseData, attachmentFiles }) => {
      await queryClient.cancelQueries({ queryKey })
      const previousExpenses = queryClient.getQueryData<Expense[]>(queryKey)

      const currentExpense = allExpenses.find(expense => expense.id === expenseData.id)

      queryClient.setQueryData<Expense[]>(queryKey, old => {
        if (!old) return old
        return old.map(expense => {
          if (expense.id === expenseData.id) {
            const base = (currentExpense || expense) as Expense
            const updatedOptimisticData: Partial<Expense> = { ...expenseData }
            if (attachmentFiles?.length) {
              updatedOptimisticData.attachmentPath = 'uploading...'
              updatedOptimisticData.attachmentURL = 'uploading...'
              updatedOptimisticData.attachmentPaths = ['uploading...']
              updatedOptimisticData.attachmentURLs = ['uploading...']
            } else if (expenseData.attachmentPath === null) {
              updatedOptimisticData.attachmentPath = null
              updatedOptimisticData.attachmentURL = null
              updatedOptimisticData.attachmentPaths = null
              updatedOptimisticData.attachmentURLs = null
            }
            const wasPaid = base.paid === true
            const isPaid = (expenseData.paid !== undefined ? expenseData.paid : base.paid) === true
            let nextPaidAt: Date | null = null
            if (!isPaid) {
              nextPaidAt = null
            } else if (expenseData.paidAt !== undefined) {
              nextPaidAt = expenseData.paidAt
            } else if (!wasPaid) {
              nextPaidAt = expenseNow()
            } else {
              nextPaidAt = base.paidAt instanceof Date ? base.paidAt : expenseNow()
            }

            const nextPlannedPaymentWindow = isPaid
              ? null
              : expenseData.plannedPaymentWindow !== undefined
                ? expenseData.plannedPaymentWindow === 'today' || expenseData.plannedPaymentWindow === 'this_week'
                  ? expenseData.plannedPaymentWindow
                  : null
                : base.plannedPaymentWindow ?? null

            return {
              ...base,
              ...updatedOptimisticData,
              paidAt: nextPaidAt,
              plannedPaymentWindow: nextPlannedPaymentWindow,
              date: expenseData.date, // Ensure date is directly from expenseData
              updated_at: new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })),
              updated_by: userInfo?.id || userInfo?.email || '',
            } as Expense
          }
          return expense
        })
      })

      return { previousExpenses }
    },
    onError: (err, variables, context) => {
      queryClient.setQueryData(queryKey, context?.previousExpenses)
      if (!options?.dontShowUpdates) {
        showError('Error updating expense')
      }
      console.error('Error updating expense:', err)
      options?.onError?.(err instanceof Error ? err : new Error(String(err)))
    },
    onSuccess: (result, variables) => {
      // Update the cache with the actual result from the server
      queryClient.setQueryData<Expense[]>(queryKey, old => {
        if (!old) return [result]
        return old.map(expense => (expense.id === result.id ? result : expense))
      })
      queryClient.invalidateQueries({ queryKey: ['expenses'] })

      if (!options?.dontShowUpdates) {
        showSuccess('Expense updated successfully')
      }
      options?.onSuccess?.()
    },
  })

  // Delete an expense
  const deleteExpense = useMutation({
    mutationFn: async (id: string) => {
      const expenseToDelete = allExpenses.find(expense => expense.id === id)

      if (expenseToDelete) {
        // Delete attachment from storage if it exists
        const attachmentPaths = getNormalizedAttachmentPaths(expenseToDelete)
        if (attachmentPaths.length) {
          await deleteExpenseAttachments(attachmentPaths)
        }
        // Update vehicle costs
        if (expenseToDelete.vehicleId) {
          await updateVehicleCosts(expenseToDelete.vehicleId, expenseToDelete.category, -expenseToDelete.amount)
        }
      }

      const expenseRef = doc(expensesCollection, id)
      await deleteDoc(expenseRef)
      return id
    },
    onMutate: async id => {
      await queryClient.cancelQueries({ queryKey })
      const previousExpenses = queryClient.getQueryData<Expense[]>(queryKey)

      queryClient.setQueryData<Expense[]>(queryKey, old => {
        if (!old) return old
        return old.filter(expense => expense.id !== id)
      })

      return { previousExpenses }
    },
    onError: (err, id, context) => {
      queryClient.setQueryData(queryKey, context?.previousExpenses)
      if (!options?.dontShowUpdates) {
        showError('Error deleting expense')
      }
      console.error('Error deleting expense:', err)
      options?.onError?.(err instanceof Error ? err : new Error(String(err)))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
      if (!options?.dontShowUpdates) {
        showSuccess('Expense deleted successfully')
      }
      options?.onSuccess?.()
    },
  })

  // Calculate total expenses by category - now using filtered expenses array.
  // Seed with built-in categories so consumers can rely on those keys; custom
  // categories are added dynamically. Only reviewed expenses count toward totals.
  const totalExpensesByCategory = expenses.reduce<Record<string, number>>(
    (acc, expense) => {
      if (!isReviewed(expense)) return acc
      const category = expense.category
      acc[category] = (acc[category] || 0) + expense.amount
      return acc
    },
    Object.fromEntries(BUILT_IN_EXPENSE_CATEGORIES.map(category => [category, 0])) as Record<string, number>,
  )

  // Calculate total expenses
  const totalExpenses = Object.values(totalExpensesByCategory).reduce((sum, amount) => sum + amount, 0)

  // --- Import invoices: create pending rows + upload PDFs for background extraction ---
  const importInvoicesMutation = useMutation({
    mutationFn: async (files: File[]) => {
      if (!userInfo) {
        throw new Error('User must be authenticated to import invoices')
      }

      const createdIds: string[] = []
      for (const file of files) {
        const now = expenseNow()
        const pendingExpense: Omit<Expense, 'id'> = {
          category: '',
          amount: 0,
          paid: false,
          paidAt: null,
          creditCardCharge: null,
          discountType: null,
          discountValue: null,
          description: '',
          date: now,
          created_by: userInfo.id || userInfo.email || '',
          created_at: now,
          updated_at: null,
          updated_by: null,
          customCategory: null,
          vehicleId: null,
          transportCompanyId: null,
          invoiceNumber: null,
          vendorCategory: null,
          businessName: '',
          attachmentPath: null,
          attachmentURL: null,
          attachmentPaths: null,
          attachmentURLs: null,
          repairSubcategories: null,
          customRepairSubcategory: null,
          repairPartsAmount: null,
          repairLaborAmount: null,
          repairFeesAmount: null,
          repairTaxAmount: null,
          repairShopId: null,
          partName: null,
          reimbursementSubcategory: null,
          reviewStatus: 'processing',
          awaitingExtraction: true,
          extractedData: null,
          extractionModel: null,
          extractionError: null,
          suggestedCategory: null,
          plannedPaymentWindow: null,
        }

        // 1. Create the pending doc first so the Storage trigger finds it on upload.
        const docRef = await addDoc(expensesCollection, pendingExpense)
        // 2. Upload the invoice — this fires the extractInvoiceListener function.
        const { attachmentPaths, attachmentURLs } = await uploadExpenseAttachments(docRef.id, [file])
        // 3. Record the attachment on the doc (does not touch reviewStatus/extracted fields).
        await updateDoc(doc(expensesCollection, docRef.id), {
          attachmentPath: attachmentPaths[0] ?? null,
          attachmentURL: attachmentURLs[0] ?? null,
          attachmentPaths,
          attachmentURLs,
        })
        createdIds.push(docRef.id)
      }
      return createdIds
    },
    onSuccess: ids => {
      refetchExpenses()
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
      showInfo(`Reading ${ids.length} invoice${ids.length === 1 ? '' : 's'}…`)
    },
    onError: (error: Error) => {
      showError(`Error importing invoice: ${error.message}`)
    },
  })

  const importRepairShopInvoiceMutation = useMutation({
    mutationFn: async (input: RepairShopInvoiceImportInput): Promise<string> => {
      if (!userInfo) {
        throw new Error('User must be authenticated to import invoices')
      }
      if (!storage) {
        throw new Error('Firebase Storage is not properly initialized.')
      }

      const snapshot = await getDocs(expensesCollection)
      const existing = snapshot.docs
        .map(docSnap => docSnap.data())
        .find(expense => expense.sourceRepairInvoiceStoragePath === input.storagePath)
      if (existing?.id) return existing.id

      const downloadUrl = await getDownloadURL(ref(storage, input.storagePath))
      const response = await fetch(downloadUrl)
      if (!response.ok) {
        throw new Error('Failed to download repair shop invoice PDF')
      }
      const blob = await response.blob()
      const fileName = input.storagePath.split('/').pop() || 'invoice.pdf'
      const file = new File([blob], fileName, { type: blob.type || 'application/pdf' })

      const now = expenseNow()
      const pendingExpense: Omit<Expense, 'id'> = {
        category: 'Repairs',
        amount: 0,
        paid: false,
        paidAt: null,
        creditCardCharge: null,
        discountType: null,
        discountValue: null,
        description: input.note?.trim() ?? '',
        date: now,
        created_by: userInfo.id || userInfo.email || '',
        created_at: now,
        updated_at: null,
        updated_by: null,
        customCategory: null,
        vehicleId: input.vehicleId,
        transportCompanyId: null,
        invoiceNumber: null,
        vendorCategory: null,
        businessName: '',
        attachmentPath: null,
        attachmentURL: null,
        attachmentPaths: null,
        attachmentURLs: null,
        repairSubcategories: null,
        customRepairSubcategory: null,
        repairPartsAmount: null,
        repairLaborAmount: null,
        repairFeesAmount: null,
        repairTaxAmount: null,
        repairShopId: input.repairShopId,
        repairTruckId: input.repairTruckId,
        sourceRepairInvoiceStoragePath: input.storagePath,
        sourceRepairTruckId: input.repairTruckId,
        partName: null,
        reimbursementSubcategory: null,
        reviewStatus: 'processing',
        awaitingExtraction: true,
        extractedData: null,
        extractionModel: null,
        extractionError: null,
        suggestedCategory: null,
        plannedPaymentWindow: null,
      }

      const docRef = await addDoc(expensesCollection, pendingExpense)
      const { attachmentPaths, attachmentURLs } = await uploadExpenseAttachments(docRef.id, [file])
      await updateDoc(doc(expensesCollection, docRef.id), {
        attachmentPath: attachmentPaths[0] ?? null,
        attachmentURL: attachmentURLs[0] ?? null,
        attachmentPaths,
        attachmentURLs,
      })
      return docRef.id
    },
    onSuccess: () => {
      refetchExpenses()
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
    },
    onError: (error: Error) => {
      showError(`Error importing repair shop invoice: ${error.message}`)
    },
  })

  const setPlannedPaymentWindow = useMutation({
    mutationFn: async ({
      expenseIds,
      plannedPaymentWindow,
    }: {
      expenseIds: string[]
      plannedPaymentWindow: PlannedPaymentWindow | null
    }) => {
      if (!userInfo) {
        throw new Error('User must be authenticated to update expenses')
      }
      if (expenseIds.length === 0) {
        return []
      }

      const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }))
      const updatedBy = userInfo.id || userInfo.email || ''

      const updates = expenseIds.map(async id => {
        const currentExpense = allExpenses.find(expense => expense.id === id)
        if (!currentExpense) {
          throw new Error(`Expense with ID ${id} not found`)
        }
        if (currentExpense.paid === true) {
          return currentExpense
        }

        const mergedExpense: Expense = {
          ...currentExpense,
          plannedPaymentWindow:
            plannedPaymentWindow === 'today' || plannedPaymentWindow === 'this_week'
              ? plannedPaymentWindow
              : null,
          updated_at: now,
          updated_by: updatedBy,
        }

        const { id: _id, ...firestoreData } = expenseConverter.toFirestore(mergedExpense)
        await updateDoc(doc(expensesCollection, id), firestoreData)
        return mergedExpense
      })

      return Promise.all(updates)
    },
    onMutate: async ({ expenseIds, plannedPaymentWindow }) => {
      await queryClient.cancelQueries({ queryKey: ['expenses'] })
      const previousCaches = queryClient.getQueriesData<Expense[]>({ queryKey: ['expenses'] })

      const applyUpdate = (old: Expense[] | undefined) => {
        if (!old) return old
        const idSet = new Set(expenseIds)
        return old.map(expense => {
          if (!idSet.has(expense.id) || expense.paid === true) return expense
          return {
            ...expense,
            plannedPaymentWindow:
              plannedPaymentWindow === 'today' || plannedPaymentWindow === 'this_week'
                ? plannedPaymentWindow
                : null,
            updated_at: new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })),
            updated_by: userInfo?.id || userInfo?.email || '',
          }
        })
      }

      queryClient.setQueriesData<Expense[]>({ queryKey: ['expenses'] }, applyUpdate)

      return { previousCaches }
    },
    onError: (err, _variables, context) => {
      context?.previousCaches?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data)
      })
      if (!options?.dontShowUpdates) {
        showError('Error updating payment schedule')
      }
      console.error('Error updating payment schedule:', err)
      options?.onError?.(err instanceof Error ? err : new Error(String(err)))
    },
    onSuccess: (_results, variables) => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
      if (!options?.dontShowUpdates) {
        const count = variables.expenseIds.length
        if (variables.plannedPaymentWindow === 'today') {
          showSuccess(`${count} expense${count === 1 ? '' : 's'} scheduled for pay today`)
        } else if (variables.plannedPaymentWindow === 'this_week') {
          showSuccess(`${count} expense${count === 1 ? '' : 's'} scheduled for pay this week`)
        } else {
          showSuccess(`Cleared payment schedule for ${count} expense${count === 1 ? '' : 's'}`)
        }
      }
      options?.onSuccess?.()
    },
  })

  const plannedPaymentExpenses = useMemo(() => getPlannedPaymentExpenses(allExpenses), [allExpenses])
  const paidThisWeekExpenses = useMemo(() => getPaidThisWeekExpenses(allExpenses), [allExpenses])
  const repairShopSpendingById = useMemo(() => getRepairShopSpendingById(allExpenses), [allExpenses])

  // --- Invoice Extraction using AI Functions ---
  const extractExpenseInfoFromAttachment = useMutation({
    mutationFn: async (file: File) => {
      options?.onExtractionStart?.()

      console.log('Extracting invoice data from attachment:', file)
      try {
        // Use the aiFunctions to extract invoice data
        const extractedData = await apiClient.portal.admin.extractInvoiceData(file)
        console.log('Extracted invoice data:', extractedData)

        // Validate the extracted data
        const validatedData = validateExtractedInvoiceData(extractedData)

        options?.onExtractionSuccess?.(validatedData)
        return validatedData
      } catch (error) {
        console.error('Error extracting data:', error)
        const err = error instanceof Error ? error : new Error('Failed to extract data from invoice')
        options?.onExtractionError?.(err)
        throw err
      }
    },
    onSuccess: data => {
      showSuccess('Invoice data extracted successfully!')
    },
    onError: (error: Error) => {
      showError(`Error extracting invoice data: ${error.message}`)
    },
  })

  return {
    expenses,
    allExpenses,
    isLoading,
    isFetching,
    isError,
    error,
    refetchExpenses,
    addExpense: addExpense.mutateAsync,
    updateExpense: updateExpense.mutateAsync,
    deleteExpense: deleteExpense.mutateAsync,
    totalExpensesByCategory,
    totalExpenses,
    extractFromAttachment: extractExpenseInfoFromAttachment.mutateAsync,
    isExtracting: extractExpenseInfoFromAttachment.isPending,
    importInvoices: importInvoicesMutation.mutateAsync,
    isImportingInvoices: importInvoicesMutation.isPending,
    importRepairShopInvoice: importRepairShopInvoiceMutation.mutateAsync,
    isImportingRepairShopInvoice: importRepairShopInvoiceMutation.isPending,
    plannedPaymentExpenses,
    paidThisWeekExpenses,
    repairShopSpendingById,
    setPlannedPaymentWindow: setPlannedPaymentWindow.mutateAsync,
    isSettingPlannedPaymentWindow: setPlannedPaymentWindow.isPending,
  }
}
